import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box, Paper, Grid, Typography, Stack, Chip, Avatar,
  Button, Divider, TextField, Alert, Snackbar,
  CircularProgress, IconButton, Tooltip, LinearProgress,
  Switch, FormControlLabel,
} from '@mui/material';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import SaveIcon           from '@mui/icons-material/Save';
import DownloadIcon       from '@mui/icons-material/Download';
import EmailIcon          from '@mui/icons-material/Email';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EditIcon           from '@mui/icons-material/Edit';
import AttachFileIcon     from '@mui/icons-material/AttachFile';
import UploadFileIcon     from '@mui/icons-material/UploadFile';
import { Link }           from 'react-router-dom';
import { useParams }      from 'react-router-dom';
import apiService, { authFetch } from '../../services/apiServices';
import SelectController    from '../../controllers/SelectController';
import InputController     from '../../controllers/InputController';
import dateFormats         from '../../services/dateFormats';
import HolidaysHistory     from './HolidaysHistory';
import MaternityStages     from '../leave/MaternityStages';
import helper              from '../../services/helper';
import globals             from '../../services/globals';
import notificationsService from '../../services/notificationsService';
import config              from '../../config/config';
import jsPDF               from 'jspdf';
import usePermissions      from '../../hooks/usePermissions';
import { getAuthToken } from '../../services/authSession';
import { leaveTypeChoices, leaveTypeColor, MATERNITY_LEAVE_TYPE_ID } from '../../config/leaveTypes';

// ─── Constants ────────────────────────────────────────────────────────────────
// Nga `config/leaveTypes.js` — i njëjti katalog si tabela `leave_types`.
// Lista e vjetër e koduar këtu i jepte numra të tjerë të njëjtave lloje.
const HOLIDAY_TYPES = [{ id: 0, name: '' }, ...leaveTypeChoices()];
// Ngjyra e llojit vjen nga katalogu; sfondi është e njëjta ngjyrë e zbehtë.
const typeMeta = (id) => ({ bg: `${leaveTypeColor(id)}1A`, color: leaveTypeColor(id) });

const initials = (n = '') =>
  n.split(' ').map(p => p[0] || '').join('').toUpperCase().slice(0, 2) || '?';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function workingDaysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  let count = 0, d = new Date(s);
  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// ─── BalanceBar ───────────────────────────────────────────────────────────────
function BalanceBar({ label, used, allowance, color = '#3E8FA3', planned = 0 }) {
  const unlimited  = !isFinite(allowance);
  const remaining  = unlimited ? Infinity : Math.max(0, allowance - used);
  const pct        = unlimited ? 0 : Math.min(100, Math.round(((used + planned) / allowance) * 100));
  const barColor   = pct >= 90 ? '#DC2626' : pct >= 65 ? '#D97706' : '#16A34A';
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" label={`${used} prd`}
            sx={{ fontSize: '0.68rem', height: 18, bgcolor: `${color}18`, color }} />
          <Chip size="small" label={unlimited ? '∞ mbtn' : `${remaining} mbtn`}
            sx={{ fontSize: '0.68rem', height: 18, bgcolor: remaining === 0 ? '#FEE2E2' : '#DCFCE7',
              color: remaining === 0 ? '#B91C1C' : '#15803D' }} />
        </Stack>
      </Stack>
      {!unlimited && (
        <LinearProgress variant="determinate" value={pct}
          sx={{ height: 5, borderRadius: 3, bgcolor: '#E5E7EB',
            '& .MuiLinearProgress-bar': { bgcolor: barColor } }} />
      )}
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EditHoliday() {
  const { id } = useParams();
  const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm();

  const { hasAnyRole } = usePermissions();
  const isEmployee = hasAnyRole(['employee']) && !hasAnyRole(['super_admin', 'company_admin', 'hr_manager', 'team_lead', 'payroll_manager']);
  const allowedTypes = isEmployee ? HOLIDAY_TYPES.filter(t => t.id === 0 || t.id === 1) : HOLIDAY_TYPES;
  const holidayIdRef = React.useRef(null); // stores the record Id — not a form field

  // State
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [employees,   setEmployees]   = useState([]);
  const [empHolidays, setEmpHolidays] = useState([]);
  const [toast,       setToast]       = useState({ open: false, msg: '', sev: 'success' });
  const [editMode,    setEditMode]    = useState(false);
  const [halfDay,     setHalfDay]     = useState(null); // null=full, 'first'=paradite, 'second'=pasdite
  const [leaveHours,  setLeaveHours]  = useState('');   // '' = not using hours mode; number string = partial hours
  const [attachmentUrl,    setAttachmentUrl]    = useState(null);   // stored filename from DB
  const [attachUploading,  setAttachUploading]  = useState(false);
  // decision: null | 'approve' | 'reject' | 'done-approve' | 'done-reject'
  const [decision, setDecision] = useState(null);

  const [POLICY, setPOLICY] = useState({
    1: { name: 'Vjetor',           allowance: 20 },
    6: { name: 'Me pagesë',        allowance: 5 },
    4: { name: 'Pa pagesë',        allowance: Infinity },
    2: { name: 'Mjekësor',         allowance: 20 },
    3: { name: 'Martesor',         allowance: 5 },
    5: { name: 'Jashtëzakonshëm', allowance: 5 },
    7: { name: 'Dhënie gjaku',     allowance: 1 },
  });

  const notificationsConfigured = Boolean(config.notificationsApiUrl);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getTypeName = (val) => {
    if (!val) return '';
    const tid = typeof val === 'object' ? (val.id ?? val.Id) : val;
    return HOLIDAY_TYPES.find(t => t.id === Number(tid))?.name || '';
  };

  const getSelectedEmployee = () => {
    const fe = watch('Employee');
    if (fe && (fe.Id || fe.id)) return fe;
    const eid = watch('EmployeeId');
    return eid ? employees.find(e => String(e.Id) === String(eid)) : null;
  };

  const currentYearUsage = useCallback(() => {
    const year = new Date().getFullYear();
    const byType = {};
    (empHolidays || []).forEach(h => {
      if (new Date(h.StartDate).getFullYear() !== year) return;
      const days = h.LeaveHours != null ? h.LeaveHours / 8 : (h.HalfDay ? 0.5 : Number(h.holidayDays || workingDaysBetween(h.StartDate, h.EndDate)));
      const t = h.HolidayType;
      byType[t] = (byType[t] || 0) + (isNaN(days) ? 0 : days);
    });
    return byType;
  }, [empHolidays]);

  const balanceInfo = useCallback((typeOverride, plannedOverride) => {
    const sel  = typeOverride || watch('HolidayType');
    const t    = typeof sel === 'object' ? (sel?.id ?? sel?.Id) : sel;
    const numT = Number(t);
    const usage  = currentYearUsage();
    const pol    = POLICY[numT];
    const used   = numT ? (usage[numT] || 0) : 0;
    const remain = pol ? (isFinite(pol.allowance) ? Math.max(0, pol.allowance - used) : Infinity) : 0;
    const planned = plannedOverride !== undefined ? plannedOverride : workingDaysBetween(watch('StartDate'), watch('EndDate'));
    const diff    = isFinite(remain) ? (remain - planned) : Infinity;
    return { t: numT, pol, used, remain, planned, diff };
  }, [currentYearUsage, POLICY, watch]);

  // ── Data loading ───────────────────────────────────────────────────────────
  const getEmployeeHolidays = useCallback((empId) => {
    if (!empId) { setEmpHolidays([]); return; }
    apiService().query('getEmployeesHolidays', 'id=' + empId)
      .then(res => { if (res?.success) setEmpHolidays(res.data || []); });
  }, []);

  const getEHoliday = useCallback((employeesList) => {
    setLoading(true);
    apiService().query('getHoliday', 'id=' + id).then(res => {
      if (res.success && res.data?.length) {
        const h = res.data[0];
        holidayIdRef.current = h.Id || h.id; // persist before field loop
        h.StartDate = dateFormats(h.StartDate);
        h.EndDate   = dateFormats(h.EndDate);
        setAttachmentUrl(h.AttachmentUrl || null);
        setLeaveHours(h.LeaveHours != null ? String(h.LeaveHours) : '');
        Object.keys(h).forEach(field => {
          if (field === 'Employee') {
            setValue('EmployeeId', h[field]);
            setValue('Employee', (employeesList || []).find(e => e.Id === h[field]) || null);
            getEmployeeHolidays(h[field]);
          } else if (field === 'HolidayType') {
            setValue('HolidayType', HOLIDAY_TYPES.find(t => t.id === h[field]) || null);
          } else {
            setValue(field, h[field]);
          }
        });
        setHalfDay(h.HalfDay || null);
        // Auto-trigger decision from URL ?decision=approve|reject
        const urlDecision = helper().getParameterByName('decision');
        if (urlDecision === 'approve' || urlDecision === 'reject') {
          setDecision(urlDecision);
        }
      }
      setLoading(false);
    });
  }, [id, getEmployeeHolidays, setValue]);

  const getEmployees = useCallback(() => {
    apiService().get('workers').then(res => {
      if (res.success) {
        setEmployees(res.data);
        getEHoliday(res.data);
      }
    });
  }, [getEHoliday]);

  useEffect(() => { getEmployees(); }, [getEmployees]);

  // Sync EndDate = StartDate when switching to half-day mode
  useEffect(() => {
    if (!halfDay) return;
    const sd = watch('StartDate');
    if (sd) setValue('EndDate', sd);
  }, [halfDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset halfDay when leave type changes away from annual (id 1)
  const _htRawForEffect = watch('HolidayType');
  const _htIdForEffect = typeof _htRawForEffect === 'object'
    ? (_htRawForEffect?.id ?? _htRawForEffect?.Id ?? 0)
    : Number(_htRawForEffect || 0);
  useEffect(() => {
    if (_htIdForEffect && _htIdForEffect !== 1) setHalfDay(null);
  }, [_htIdForEffect]);

  // Ripërdor id-në tashmë të nxjerrë më lart — lehonia dallohet nga katalogu,
  // jo nga një numër i shkruar këtu.
  const isMaternity = _htIdForEffect === MATERNITY_LEAVE_TYPE_ID;

  useEffect(() => {
    globals().getSettings().then(s => {
      if (!s) return;
      const up = Number(s.UnpaidLeaveAllowance);
      setPOLICY({
        1: { name: 'Vjetor',           allowance: Number(s.AnnualLeaveAllowance) || 20 },
        6: { name: 'Me pagesë',        allowance: Number(s.PaidLeaveAllowance) || 5 },
        4: { name: 'Pa pagesë',        allowance: up > 0 ? up : Infinity },
        2: { name: 'Mjekësor',         allowance: Number(s.MedicalLeaveAllowance) || 20 },
        3: { name: 'Martesor',         allowance: Number(s.MaritalLeaveAllowance) || 5 },
        5: { name: 'Jashtëzakonshëm', allowance: Number(s.ExceptionalLeaveAllowance) || 5 },
        7: { name: 'Dhënie gjaku',     allowance: Number(s.BloodDonationLeaveAllowance) || 1 },
      });
    });
  }, []);

  // ── Attachment upload ──────────────────────────────────────────────────────
  const handleAttachmentChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      setToast({ open: true, msg: 'Lejohen vet\u00ebm skedar\u00ebt PDF, JPG dhe PNG.', sev: 'error' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setToast({ open: true, msg: 'Skedari nuk duhet t\u00eb kaloj\u00eb 5 MB.', sev: 'error' });
      return;
    }
    const hId = holidayIdRef.current || id;
    if (!hId) { setToast({ open: true, msg: 'Ruaj k\u00ebrkes\u00ebn s\u00eb pari.', sev: 'warning' }); return; }
    setAttachUploading(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('attachment', file);
      const apiBase = process.env.REACT_APP_API_URL || '';
      const resp = await fetch(`${apiBase}/api/holidays/${hId}/upload-attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const json = await resp.json();
      if (json.success) {
        setAttachmentUrl(json.data.AttachmentUrl);
        setToast({ open: true, msg: 'Dokumenti u ngarkua me sukses!', sev: 'success' });
      } else {
        setToast({ open: true, msg: json.error || 'Gabim gjat\u00eb ngarkimit.', sev: 'error' });
      }
    } catch (err) {
      setToast({ open: true, msg: err.message || 'Gabim lidhës.', sev: 'error' });
    } finally {
      setAttachUploading(false);
      e.target.value = '';
    }
  };

  // ── Attachment download (medical document) ──────────────────────────────────
  const handleDownloadAttachment = async () => {
    const hId = holidayIdRef.current || id;
    if (!hId) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const resp = await authFetch(`${apiBase}/api/holidays/${hId}/attachment`);
      if (!resp.ok) { setToast({ open: true, msg: 'Dokumenti nuk u gjet.', sev: 'error' }); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setToast({ open: true, msg: 'Gabim gjatë shkarkimit të dokumentit.', sev: 'error' });
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const buildPayload = (data) => {
    const p = { ...data };
    // Ensure Id is always present — handleSubmit strips unregistered fields
    if (!p.Id && !p.id) p.Id = holidayIdRef.current;
    p.Approve = Boolean(data.Approve);
    const empVal  = data.Employee?.Id || data.Employee?.id || data.EmployeeId || data.Employee;
    if (empVal) p.Employee = empVal;
    const typeVal = data.HolidayType?.id ?? data.HolidayType?.Id ?? data.HolidayType;
    if (typeVal != null) p.HolidayType = typeVal;
    p.HalfDay = halfDay || null;
    if (halfDay) p.HolidayDays = 0.5;
    p.LeaveHours = leaveHours ? parseFloat(leaveHours) : null;
    if (leaveHours && parseFloat(leaveHours) > 0) p.HolidayDays = +(parseFloat(leaveHours) / 8).toFixed(3);
    p.CreatedBy = p.CreatedBy || '';
    return p;
  };

  const onSubmit = (data) => {
    setSaving(true);
    apiService().put('holidays', buildPayload(data))
      .then(res => {
        setSaving(false);
        if (res.success) {
          setToast({ open: true, msg: 'U ruajt me sukses!', sev: 'success' });
          setEditMode(false);
        } else {
          setToast({ open: true, msg: 'Ka ndodhur një gabim!', sev: 'error' });
        }
      })
      .catch(err => {
        setSaving(false);
        setToast({ open: true, msg: err?.message || 'Ka ndodhur një gabim!', sev: 'error' });
      });
  };

  // ── Decision confirm ───────────────────────────────────────────────────────
  const handleDecisionConfirm = () => {
    const isApprove = decision === 'approve';
    setValue('Approve', isApprove);
    setSaving(true);
    apiService().put('holidays', buildPayload({ ...watch(), Approve: isApprove }))
      .then(res => {
        setSaving(false);
        if (res.success) {
          setDecision(isApprove ? 'done-approve' : 'done-reject');
          setToast({ open: true, msg: isApprove ? 'Pushimi u aprovua!' : 'Pushimi u refuzua!', sev: 'success' });
          trySendDecisionEmail({
            approve: isApprove, employee: getSelectedEmployee(),
            start: watch('StartDate'), end: watch('EndDate'),
            type: watch('HolidayType'), id: watch('Id') || id,
            description: watch('Description'),
          });
        } else {
          setToast({ open: true, msg: 'Ka ndodhur një gabim!', sev: 'error' });
          setDecision(null);
        }
      })
      .catch(err => {
        setSaving(false);
        setToast({ open: true, msg: err?.message || 'Ka ndodhur një gabim!', sev: 'error' });
        setDecision(null);
      });
  };

  // ── PDF ────────────────────────────────────────────────────────────────────
  const buildPdfDetails = (approve) => {
    const emp     = getSelectedEmployee();
    const start   = watch('StartDate');
    const end     = watch('EndDate');
    const type    = watch('HolidayType');
    const planned = workingDaysBetween(start, end);
    const { diff } = balanceInfo(type);
    return {
      employeeName:   emp ? `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() : '—',
      employeeRole:   emp?.Position || emp?.JobTitle || '—',
      employeeDept:   emp?.Department || '—',
      employeeEmail:  emp?.Email || '—',
      typeName:       getTypeName(type) || 'Pushim',
      startFmt:       fmtDate(start),
      endFmt:         fmtDate(end),
      plannedDays:    planned,
      approve:        Boolean(approve),
      description:    watch('Description') || '',
      requestCode:    watch('Id') || id || '—',
      decisionDate:   fmtDate(new Date()),
      remaining:      isFinite(diff) ? `${Math.max(0, Math.round(diff))} ditë` : 'Pa kufi',
    };
  };

  const renderPdf = (d) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const m = 20, cw = 170;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
    doc.text('Vendim për kërkesën e pushimit', 105, 25, null, null, 'center');
    doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
    doc.text(`Data e vendimit: ${d.decisionDate}`, m, 40);
    doc.text(`Nr. reference: ${d.requestCode}`, m, 47);
    // Employee box
    doc.setFillColor(245, 248, 255); doc.setDrawColor(210, 215, 230);
    doc.roundedRect(m, 55, cw, 36, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setTextColor(33, 33, 33);
    doc.text('Të dhënat e punëtorit', m + 3, 63);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(`Emri: ${d.employeeName}`, m + 3, 71);
    doc.text(`Pozita: ${d.employeeRole}`, m + 3, 79);
    doc.text(`Departamenti: ${d.employeeDept}`, m + 3, 87);
    // Leave box
    const lt = 99;
    doc.setFillColor(250, 250, 250); doc.setDrawColor(210, 215, 230);
    doc.roundedRect(m, lt, cw, 46, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setTextColor(33, 33, 33);
    doc.text('Detajet e pushimit', m + 3, lt + 8);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(`Lloji: ${d.typeName}`, m + 3, lt + 16);
    doc.text(`Periudha: ${d.startFmt} - ${d.endFmt}`, m + 3, lt + 24);
    doc.text(`Ditë pune: ${d.plannedDays}`, m + 3, lt + 32);
    doc.text(`Ditë të mbetura: ${d.remaining}`, m + 3, lt + 40);
    // Decision box
    const dt = lt + 54;
    const ok = d.approve;
    doc.setFillColor(ok ? 220 : 255, ok ? 252 : 228, ok ? 231 : 228);
    doc.setDrawColor(ok ? 22 : 220, ok ? 163 : 38, ok ? 74 : 38);
    doc.roundedRect(m, dt, cw, 26, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.setTextColor(ok ? 21 : 185, ok ? 128 : 28, ok ? 61 : 28);
    doc.text(`Vendimi: ${ok ? 'APROVOHET' : 'REFUZOHET'}`, m + 3, dt + 10);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(ok ? 'Kërkesa aprovohet sipas politikave të kompanisë.' : 'Kërkesa refuzohet sipas politikave të kompanisë.', m + 3, dt + 18);
    // Comment
    if (d.description) {
      const ct = dt + 34;
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(33, 33, 33);
      doc.text('Komente:', m, ct);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
      doc.text(doc.splitTextToSize(d.description, cw), m, ct + 8);
    }
    return doc;
  };

  const handleDownloadPdf = () => {
    const approve  = decision === 'approve' || decision === 'done-approve' || Boolean(watch('Approve'));
    const details  = buildPdfDetails(approve);
    const doc      = renderPdf(details);
    const empClean = (details.employeeName || 'Punetor').replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '');
    doc.save(`Vendim_Pushimi_${empClean}.pdf`);
    setToast({ open: true, msg: 'PDF u shkarkua!', sev: 'info' });
  };

  const trySendDecisionEmail = ({ approve, employee, start, end, type, id: hid, description }) => {
    if (!employee?.Email) return;
    const typeName    = getTypeName(type);
    const decisionTxt = approve ? 'Aprovuar' : 'Refuzuar';
    const details     = buildPdfDetails(approve);
    const doc         = renderPdf(details);
    const base64      = doc.output('datauristring').split(',')[1];
    const fname       = `Vendim_Pushimi_${(details.employeeName || 'Punetor').replace(/\s+/g, '_').replace(/[^A-Za-z0-9_]/g, '')}.pdf`;
    const subject     = `Vendim për pushim (${typeName}) - ${decisionTxt}`;
    const body        = [`Përshëndetje ${employee.FirstName || ''},`, '', 'Vendimi zyrtar gjendet në PDF-në e bashkangjitur.', 'Kontaktoni BNJ-në për pyetje shtesë.', '', 'Faleminderit.'].join('\n');
    if (notificationsConfigured) {
      notificationsService.sendEmail({
        to: [employee.Email], subject, body,
        attachments: [{ filename: fname, name: fname, content: base64, encoding: 'base64', contentType: 'application/pdf' }],
      }).catch(() => {
        doc.save(fname);
        window.location.href = `mailto:${encodeURIComponent(employee.Email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      });
    } else {
      doc.save(fname);
      window.location.href = `mailto:${encodeURIComponent(employee.Email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const emp       = getSelectedEmployee();
  const empName   = emp ? `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() : '';
  const htRaw     = watch('HolidayType');
  const htId      = typeof htRaw === 'object' ? (htRaw?.id ?? htRaw?.Id) : Number(htRaw);
  const htName    = getTypeName(htRaw);
  const htMeta    = typeMeta(htId) || { bg: '#F3F4F6', color: '#6B7280' };
  const startDate = watch('StartDate');
  const endDate   = watch('EndDate');
  const planned   = leaveHours ? +(parseFloat(leaveHours) / 8).toFixed(3) : (halfDay ? 0.5 : workingDaysBetween(startDate, endDate));
  const isApproved = Boolean(watch('Approve'));
  const bal        = balanceInfo(null, leaveHours ? +(parseFloat(leaveHours) / 8).toFixed(3) : (halfDay ? 0.5 : undefined));

  const decisionPending = decision === 'approve' || decision === 'reject';
  const decisionDone    = decision === 'done-approve' || decision === 'done-reject';
  const decisionApprove = decision === 'approve' || decision === 'done-approve';

  return (
    <Box>
      {/* Toast */}
      <Snackbar
        open={toast.open} autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.sev} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ fontSize: '0.82rem' }}>
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton size="small" component={Link} to="/inbox" sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <EventAvailableIcon sx={{ color: '#0369A1', fontSize: 18 }} />
        <Typography variant="subtitle1" fontWeight={700}>Kërkesë pushimi</Typography>
        {!loading && (
          <Chip size="small"
            label={isApproved ? 'Aprovuar' : 'Në pritje'}
            sx={{
              height: 20, fontSize: '0.72rem', fontWeight: 700, border: 'none',
              bgcolor: isApproved ? '#DCFCE7' : '#FFFBEB',
              color:   isApproved ? '#15803D' : '#D97706',
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {!loading && (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Shkarko PDF">
              <IconButton size="small" onClick={handleDownloadPdf} sx={{ color: 'text.secondary' }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dërgo email">
              <IconButton size="small" sx={{ color: 'text.secondary' }}
                onClick={() => trySendDecisionEmail({
                  approve: isApproved, employee: emp,
                  start: startDate, end: endDate,
                  type: htRaw, id: watch('Id') || id,
                  description: watch('Description'),
                })}>
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ color: '#0369A1' }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* ── Left: Decision + Form ── */}
          <Grid item xs={12} md={8}>

            {/* Decision banner (inline, no dialog) */}
            {decisionPending && !decisionDone && (
              <Paper elevation={0} sx={{
                mb: 2, borderRadius: 2, overflow: 'hidden',
                border: '2px solid',
                borderColor: decisionApprove ? '#16A34A' : '#DC2626',
                bgcolor: decisionApprove ? '#F0FDF4' : '#FFF1F2',
              }}>
                <Box sx={{ px: 2.5, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                    {decisionApprove
                      ? <CheckCircleIcon sx={{ color: '#16A34A', fontSize: 22 }} />
                      : <CancelIcon     sx={{ color: '#DC2626', fontSize: 22 }} />
                    }
                    <Typography variant="subtitle1" fontWeight={700}
                      sx={{ color: decisionApprove ? '#15803D' : '#B91C1C' }}>
                      {decisionApprove ? 'Konfirmo aprovimin' : 'Konfirmo refuzimin'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {decisionApprove
                      ? `Jeni duke aprovuar pushimin e ${empName || 'punëtorit'} për periudhën ${fmtDate(startDate)} → ${fmtDate(endDate)} (${planned} ditë pune).`
                      : `Jeni duke refuzuar pushimin e ${empName || 'punëtorit'} për periudhën ${fmtDate(startDate)} → ${fmtDate(endDate)}.`
                    }
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button variant="contained" size="small" disabled={saving}
                      startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                      onClick={handleDecisionConfirm}
                      sx={{
                        bgcolor: decisionApprove ? '#16A34A' : '#DC2626',
                        '&:hover': { bgcolor: decisionApprove ? '#15803D' : '#B91C1C' },
                        fontWeight: 700, borderRadius: 1.5, px: 2,
                      }}>
                      {saving ? 'Duke ruajtur…' : (decisionApprove ? '✓  Po, aprovo' : '✗  Po, refuzo')}
                    </Button>
                    <Button variant="text" size="small" color="inherit"
                      onClick={() => setDecision(null)} disabled={saving}>
                      Anulo
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* Decision done feedback */}
            {decisionDone && (
              <Alert severity={decisionApprove ? 'success' : 'error'} sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
                action={<Button size="small" component={Link} to="/inbox" color="inherit">Kthehu</Button>}>
                {decisionApprove ? 'Pushimi u aprovua me sukses!' : 'Pushimi u refuzua.'}
              </Alert>
            )}

            {/* Main form card */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              {/* Card header */}
              <Box sx={{
                px: 2.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#FAFCFE',
              }}>
                <EditIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="subtitle2" fontWeight={700}>Detajet e kërkesës</Typography>
                <Box sx={{ flex: 1 }} />
                {!editMode ? (
                  <Button size="small" startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                    onClick={() => setEditMode(true)}
                    sx={{ fontSize: '0.76rem', py: 0.4, px: 1.25, borderRadius: 1.5 }}>
                    Ndrysho
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="text" color="inherit" onClick={() => setEditMode(false)}
                      sx={{ fontSize: '0.76rem', py: 0.4, borderRadius: 1.5 }}>
                      Anulo
                    </Button>
                    <Button size="small" variant="contained"
                      startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 13 }} />}
                      onClick={handleSubmit(onSubmit)} disabled={saving}
                      sx={{ fontSize: '0.76rem', py: 0.4, px: 1.5, borderRadius: 1.5 }}>
                      Ruaj
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Form body */}
              <Box sx={{ p: 2.5 }}>
                {editMode ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <SelectController
                        placeholder="Lloji i pushimit"
                        error={errors.HolidayType} control={control}
                        options={allowedTypes} name="HolidayType"
                        register={{ ...register('HolidayType') }}
                        disabled={isEmployee}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectController
                        placeholder="Punëtori"
                        error={errors.Employee} control={control}
                        options={employees} name="Employee"
                        register={{ ...register('Employee') }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputController
                        placeholder="Data e fillimit" error={errors.StartDate}
                        control={control} type="date"
                        register={{ ...register('StartDate') }}
                      />
                    </Grid>
                    {!halfDay && !leaveHours && (
                      <Grid item xs={12} sm={6}>
                        <InputController
                          placeholder="Data e mbarimit" error={errors.EndDate}
                          control={control} type="date"
                          register={{ ...register('EndDate') }}
                        />
                      </Grid>
                    )}
                    {/* Half-day / Partial-hour options — annual leave only */}
                    {htId === 1 && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 1, bgcolor: '#F0F9FF', borderRadius: 1.5, border: '1px solid #BAE6FD' }}>
                          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={0.75}>
                            <FormControlLabel
                              sx={{ ml: 0, mr: 0 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={halfDay !== null}
                                  onChange={e => {
                                    const next = e.target.checked ? 'first' : null;
                                    setLeaveHours('');
                                    setHalfDay(next);
                                    if (next && watch('StartDate')) setValue('EndDate', watch('StartDate'));
                                  }}
                                  color="primary"
                                />
                              }
                              label={<Typography variant="body2" fontWeight={600}>½ Ditë</Typography>}
                            />
                            {halfDay !== null && (
                              <Stack direction="row" spacing={0.5}>
                                <Button size="small"
                                  variant={halfDay === 'first' ? 'contained' : 'outlined'}
                                  onClick={() => setHalfDay('first')}
                                  sx={{ py: 0.25, px: 1.5, fontSize: '0.72rem', borderRadius: 1.5 }}
                                >Paradite</Button>
                                <Button size="small"
                                  variant={halfDay === 'second' ? 'contained' : 'outlined'}
                                  onClick={() => setHalfDay('second')}
                                  sx={{ py: 0.25, px: 1.5, fontSize: '0.72rem', borderRadius: 1.5 }}
                                >Pasdite</Button>
                              </Stack>
                            )}
                            {halfDay === null && (
                              <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                  <Typography variant="body2" fontWeight={600} color="text.secondary">Orë:</Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={leaveHours}
                                    onChange={e => {
                                      const v = e.target.value;
                                      setLeaveHours(v);
                                      if (v && parseFloat(v) > 0) setHalfDay(null);
                                    }}
                                    onBlur={e => {
                                      const v = parseFloat(e.target.value);
                                      if (!isNaN(v)) setLeaveHours(String(Math.min(7.5, Math.max(0.5, Math.round(v * 2) / 2))));
                                    }}
                                    inputProps={{ min: 0.5, max: 7.5, step: 0.5 }}
                                    placeholder="0"
                                    sx={{ width: 80, '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                                  />
                                  {leaveHours && (
                                    <Typography variant="caption" color="text.secondary">
                                      ≈ {(parseFloat(leaveHours) / 8).toFixed(2)} ditë
                                    </Typography>
                                  )}
                                </Stack>
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Komenti / Arsyeja" multiline rows={3}
                        value={watch('Description') || ''}
                        onChange={e => setValue('Description', e.target.value)}
                        placeholder="Shto koment ose arsye…"
                        sx={{ '& .MuiInputBase-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  /* Read-only summary */
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Lloji</Typography>
                      {htName ? (
                        <Chip size="small" label={htName}
                          sx={{ height: 22, fontSize: '0.75rem', fontWeight: 700,
                            bgcolor: htMeta.bg, color: htMeta.color, border: 'none' }} />
                      ) : <Typography variant="body2">—</Typography>}
                      {halfDay && (
                        <Chip size="small"
                          label={halfDay === 'first' ? '½ Paradite' : '½ Pasdite'}
                          sx={{ mt: 0.5, height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#E0F2FE', color: '#0369A1', display: 'block', width: 'fit-content' }} />
                      )}
                      {leaveHours && (
                        <Chip size="small"
                          label={`${leaveHours}h orë`}
                          sx={{ mt: 0.5, height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#FEF3C7', color: '#92400E', display: 'block', width: 'fit-content' }} />
                      )}
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>Fillimi</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmtDate(startDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>Mbarimi</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmtDate(endDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>Ditë pune</Typography>
                      <Typography variant="body2" fontWeight={700} color="#0369A1" fontSize="1rem">
                        {leaveHours ? `${leaveHours}h` : (planned === 0.5 ? '½' : (planned || 0))}
                      </Typography>
                    </Grid>
                    {watch('Description') && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>Komenti</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{watch('Description')}</Typography>
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* Balance strip */}
                {htId > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} mb={0.75} display="block">
                      Balanca e lejes — {htName}
                    </Typography>
                    <BalanceBar
                      label={htName} used={bal.used}
                      allowance={bal.pol?.allowance ?? Infinity}
                      planned={bal.planned} color={htMeta.color}
                    />
                    {isFinite(bal.diff) && (
                      <Typography variant="caption" sx={{ mt: 0.75, display: 'block', fontWeight: 600,
                        color: bal.diff >= 0 ? '#15803D' : '#B91C1C' }}>
                        {bal.diff >= 0
                          ? `Mbeten ${bal.diff} ditë pas këtij pushimi`
                          : `⚠ Tejkalon balancën me ${Math.abs(bal.diff)} ditë`
                        }
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Attachment panel — sick leave: allow upload; any type: show download if exists */}
                {(htId === 2 || attachmentUrl) && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FFF7F0', borderRadius: 1.5, border: '1px solid #FDBA74' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
                      <AttachFileIcon sx={{ fontSize: 15, color: '#EA580C' }} />
                      <Typography variant="caption" fontWeight={700} color="#EA580C">Dokumenti mjekësor</Typography>
                    </Stack>
                    {attachmentUrl && (
                      <Stack direction="row" alignItems="center" spacing={1} mb={editMode && htId === 2 ? 1 : 0}>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Dokumenti është ngarkuar</Typography>
                        <Button size="small"
                          startIcon={<DownloadIcon sx={{ fontSize: 12 }} />}
                          onClick={handleDownloadAttachment}
                          sx={{ py: 0.3, px: 1.25, fontSize: '0.72rem', borderRadius: 1.5 }}
                        >Shiko / Shkarko</Button>
                      </Stack>
                    )}
                    {editMode && htId === 2 && (
                      <Button component="label" size="small" variant="outlined"
                        startIcon={attachUploading ? <CircularProgress size={12} color="inherit" /> : <UploadFileIcon sx={{ fontSize: 14 }} />}
                        disabled={attachUploading}
                        sx={{ fontSize: '0.72rem', borderRadius: 1.5, color: '#EA580C', borderColor: '#FDBA74' }}
                      >
                        {attachmentUrl ? 'Zëvendëso dokumentin' : 'Ngarko dokumentin (PDF, JPG, PNG)'}
                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={handleAttachmentChange} />
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Approve / Reject footer — only show when no decision done */}
              {!decisionDone && (
                <Box sx={{
                  px: 2.5, py: 1.5,
                  borderTop: '1px solid', borderColor: 'divider',
                  bgcolor: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Vendosi:</Typography>
                  <Button variant={decision === 'approve' ? 'contained' : 'outlined'} size="small" color="success"
                    startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setDecision('approve')}
                    sx={{ fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', minWidth: 100 }}>
                    Aprovo
                  </Button>
                  <Button variant={decision === 'reject' ? 'contained' : 'outlined'} size="small" color="error"
                    startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setDecision('reject')}
                    sx={{ fontWeight: 600, borderRadius: 1.5, fontSize: '0.78rem', minWidth: 100 }}>
                    Refuzo
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── Right: Employee info + all balances ── */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#F8FAFC' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Punëtori</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {emp ? (
                  <Stack spacing={1.75}>
                    {/* Avatar + name */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ width: 44, height: 44, bgcolor: '#0369A1', fontWeight: 700, fontSize: '1rem' }}>
                        {initials(empName)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>{empName}</Typography>
                        {(emp.Position || emp.JobTitle) && (
                          <Typography variant="caption" color="text.secondary">{emp.Position || emp.JobTitle}</Typography>
                        )}
                        {emp.Department && (
                          <Typography variant="caption" color="text.secondary" display="block">{emp.Department}</Typography>
                        )}
                      </Box>
                    </Stack>
                    {emp.Email && (
                      <>
                        <Divider />
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {emp.Email}
                        </Typography>
                      </>
                    )}

                    {/* All leave balances */}
                    <Divider />
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Gjendja e lejeve ({new Date().getFullYear()})
                    </Typography>
                    <Stack spacing={1.25}>
                      {Object.entries(POLICY).map(([typeId, pol]) => {
                        const usage = currentYearUsage();
                        const used  = usage[typeId] || 0;
                        const meta  = typeMeta(typeId) || { color: '#6B7280' };
                        return (
                          <BalanceBar key={typeId}
                            label={pol.name}
                            used={used}
                            allowance={pol.allowance}
                            planned={Number(typeId) === htId ? planned : 0}
                            color={meta.color}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">Nuk u gjet punëtori.</Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Lehonia — një kërkesë, tri etapa.
          Shfaqet vetëm për llojin e lehonisë: për çdo lloj tjetër ky panel nuk
          ka çka thotë. Id-ja e llojit vjen nga `config/leaveTypes.js`, i vetmi
          katalog — një literal këtu do të shkëputej sapo të ndryshonte tabela. */}
      {isMaternity && id && (
        <Box mt={2}>
          <MaternityStages
            holidayId={Number(id)}
            startDate={watch('StartDate') || null}
            canManage={!isEmployee}
          />
        </Box>
      )}

      {/* Holiday history */}
      {watch('EmployeeId') && (
        <Box mt={2}>
          <HolidaysHistory id={watch('EmployeeId')} filter={true} />
        </Box>
      )}
    </Box>
  );
}
